import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  Sparkles,
  Target, 
  Lightbulb, 
  Globe, 
  BarChart3,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

interface Trend {
  title: string;
  description: string;
  impact: 'positive' | 'negative' | 'neutral';
}

interface Opportunity {
  sector: string;
  description: string;
  countries: string[];
}

interface MarketInsights {
  hotSectors: string[];
  emergingMarkets: string[];
  avgDealSize: string;
}

interface Analysis {
  summary: string;
  trends: Trend[];
  opportunities: Opportunity[];
  recommendations: string[];
  marketInsights: MarketInsights | null;
}

interface DataStats {
  totalAnalyzed: number;
  withValue: number;
  avgValue: number;
  topCountries: string[];
  topSectors: string[];
}

export default function TrendsAnalysis() {
  const { t } = useTranslation();
  const { role } = useAuth();
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [dataStats, setDataStats] = useState<DataStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Only show for admin users
  if (role !== 'admin') {
    return null;
  }

  const fetchAnalysis = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('analyze-trends');

      if (fnError) {
        throw new Error(fnError.message);
      }

      if (data.error) {
        setError(data.error);
        return;
      }

      if (data.message && !data.analysis) {
        setError(data.message);
        return;
      }

      setAnalysis(data.analysis);
      setDataStats(data.dataStats);
      toast.success('Analisi completata!');
    } catch (err) {
      console.error('Error fetching analysis:', err);
      setError(err instanceof Error ? err.message : 'Errore durante l\'analisi');
      toast.error('Errore durante l\'analisi');
    } finally {
      setLoading(false);
    }
  };

  const getImpactIcon = (impact: string) => {
    switch (impact) {
      case 'positive':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'negative':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      default:
        return <Minus className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'positive':
        return 'bg-green-500/10 text-green-700 border-green-200';
      case 'negative':
        return 'bg-red-500/10 text-red-700 border-red-200';
      default:
        return 'bg-yellow-500/10 text-yellow-700 border-yellow-200';
    }
  };

  if (!analysis && !loading && !error) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Analisi Tendenze AI
          </CardTitle>
          <CardDescription>
            Usa l'intelligenza artificiale per analizzare i pattern nei bandi pubblici
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <BarChart3 className="w-8 h-8 text-primary" />
          </div>
          <p className="text-muted-foreground text-center max-w-md">
            Clicca per generare un'analisi AI dei tuoi bandi. L'AI identificherà trend, opportunità e consigli personalizzati.
          </p>
          <Button onClick={fetchAnalysis} className="gap-2">
            <Sparkles className="w-4 h-4" />
            Genera Analisi
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary animate-pulse" />
            Analisi in corso...
          </CardTitle>
          <CardDescription>
            L'AI sta analizzando i tuoi dati
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Skeleton className="h-24 w-full" />
          <div className="grid md:grid-cols-2 gap-4">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-destructive" />
            Analisi non disponibile
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-8 gap-4">
          <p className="text-muted-foreground text-center">{error}</p>
          <Button onClick={fetchAnalysis} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Riprova
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Analisi Tendenze AI
          </h2>
          {dataStats && (
            <p className="text-sm text-muted-foreground">
              Basata su {dataStats.totalAnalyzed} bandi analizzati
            </p>
          )}
        </div>
        <Button onClick={fetchAnalysis} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Aggiorna
        </Button>
      </div>

      {/* Summary */}
      {analysis?.summary && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-primary/5 to-accent/5">
          <CardContent className="p-6">
            <p className="text-lg leading-relaxed">{analysis.summary}</p>
          </CardContent>
        </Card>
      )}

      {/* Trends and Opportunities Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Trends */}
        {analysis?.trends && analysis.trends.length > 0 && (
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                Trend Identificati
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {analysis.trends.map((trend, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg border ${getImpactColor(trend.impact)}`}
                >
                  <div className="flex items-start gap-2">
                    {getImpactIcon(trend.impact)}
                    <div>
                      <p className="font-medium text-sm">{trend.title}</p>
                      <p className="text-xs mt-1 opacity-80">{trend.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Opportunities */}
        {analysis?.opportunities && analysis.opportunities.length > 0 && (
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="w-4 h-4 text-primary" />
                Opportunità
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {analysis.opportunities.map((opp, index) => (
                <div key={index} className="p-3 rounded-lg bg-muted/50">
                  <p className="font-medium text-sm">{opp.sector}</p>
                  <p className="text-xs text-muted-foreground mt-1">{opp.description}</p>
                  {opp.countries && opp.countries.length > 0 && (
                    <div className="flex gap-1 mt-2">
                      {opp.countries.map((country) => (
                        <Badge key={country} variant="outline" className="text-xs">
                          {country}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recommendations */}
      {analysis?.recommendations && analysis.recommendations.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-primary" />
              Consigli
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {analysis.recommendations.map((rec, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs shrink-0 mt-0.5">
                    {index + 1}
                  </span>
                  {rec}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Market Insights */}
      {analysis?.marketInsights && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="w-4 h-4 text-primary" />
              Insights di Mercato
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-3 gap-4">
              {analysis.marketInsights.hotSectors && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Settori caldi</p>
                  <div className="flex flex-wrap gap-1">
                    {analysis.marketInsights.hotSectors.map((sector) => (
                      <Badge key={sector} className="text-xs">
                        {sector}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {analysis.marketInsights.emergingMarkets && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Mercati emergenti</p>
                  <div className="flex flex-wrap gap-1">
                    {analysis.marketInsights.emergingMarkets.map((market) => (
                      <Badge key={market} variant="outline" className="text-xs">
                        {market}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {analysis.marketInsights.avgDealSize && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Fascia valore tipica</p>
                  <p className="text-sm font-medium">{analysis.marketInsights.avgDealSize}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
